import { useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Upload, Check, X } from 'lucide-react'
import useMutationUploadLogo from '@/core/api/hooks/useMutationUploadLogo'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useQueryVersionedUrl from '@/common/hooks/useQueryVersionedUrl'

interface LogoUploadBoxProps {
	projectName: string
	currentLogoUrl?: string
}

export default function LogoUploadBox({
	projectName,
	currentLogoUrl,
}: LogoUploadBoxProps) {
	// Generate default logo URL
	const defaultLogoUrl = `https://image.panda-storage.com/${projectName}/logo.png`
	const logoUrlToFetch = currentLogoUrl || defaultLogoUrl

	const queryClient = useQueryClient()
	const { data: logoUrl, isLoading: isLoadingImage } =
		useQueryVersionedUrl(logoUrlToFetch)

	const [isUploading, setIsUploading] = useState(false)
	const [previewUrl, setPreviewUrl] = useState<string>('')
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { mutateAsync: uploadLogo } = useMutationUploadLogo()

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		// Validate file type - only PNG
		if (file.type !== 'image/png') {
			showErrorToast('กรุณาเลือกไฟล์ PNG เท่านั้น')
			return
		}

		// Validate file size (10MB max)
		const maxSize = 10 * 1024 * 1024
		if (file.size > maxSize) {
			showErrorToast('ขนาดไฟล์ใหญ่เกินไป (สูงสุด 10MB)')
			return
		}

		// Store the file for later upload
		setSelectedFile(file)

		// Create preview
		const reader = new FileReader()
		reader.onload = (e) => {
			setPreviewUrl(e.target?.result as string)
		}
		reader.readAsDataURL(file)
	}

	const handleConfirmUpload = async () => {
		if (!selectedFile) return

		try {
			setIsUploading(true)

			// Upload logo directly to backend
			await uploadLogo(selectedFile)

			// Invalidate query to refetch with new ETag
			await queryClient.invalidateQueries({
				queryKey: ['versionedUrl', logoUrlToFetch],
			})

			// Clear preview and selected file after successful upload
			setPreviewUrl('')
			setSelectedFile(null)

			toast.success(
				'โลโก้ถูกอัปโหลดเรียบร้อยแล้ว กรุณารอสักครู่เพื่อให้การเปลี่ยนแปลงมีผลในระบบ',
			)

			// Reset file input
			if (fileInputRef.current) {
				fileInputRef.current.value = ''
			}
		} catch (error) {
			console.error('Logo upload failed:', error)
			showErrorToast('ไม่สามารถอัปโหลดโลโก้ได้ กรุณาลองใหม่อีกครั้ง')
		} finally {
			setIsUploading(false)
		}
	}

	const handleClearPreview = () => {
		setPreviewUrl('')
		setSelectedFile(null)
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const handleUploadClick = () => {
		fileInputRef.current?.click()
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>ตั้งค่าโลโก้สำหรับเว็บไซต์</CardTitle>
				<CardDescription>อัปโหลดโลโก้ใหม่สำหรับ {projectName}</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{/* Loading state for initial image */}
				{isLoadingImage && !previewUrl && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex h-32 items-center justify-center rounded-lg border bg-muted/50 p-4">
							<p className="text-sm text-muted-foreground">กำลังโหลด...</p>
						</div>
					</div>
				)}

				{/* Display current logo */}
				{logoUrl && !previewUrl && !isLoadingImage && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex items-center justify-center rounded-lg border bg-muted/50 p-4">
							<img
								src={logoUrl}
								alt="Current Logo"
								className="max-h-32 object-contain"
							/>
						</div>
						<p className="text-sm text-muted-foreground">โลโก้ปัจจุบัน</p>
					</div>
				)}

				{/* Display preview */}
				{previewUrl && (
					<div className="flex flex-col items-center space-y-2">
						<div className="flex items-center justify-center rounded-lg border-2 border-primary/50 bg-muted/50 p-4">
							<img
								src={previewUrl}
								alt="Preview Logo"
								className="max-h-32 object-contain"
							/>
						</div>
						<p className="text-sm font-medium text-primary">
							ตัวอย่างโลโก้ใหม่
						</p>
					</div>
				)}

				{/* Action buttons */}
				{!previewUrl ? (
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="w-full"
						onClick={handleUploadClick}
						disabled={isUploading || isLoadingImage}
					>
						<Upload className="mr-2 h-4 w-4" />
						{logoUrl ? 'เปลี่ยนโลโก้' : 'อัปโหลดโลโก้ใหม่'}
					</Button>
				) : (
					<div className="flex gap-2">
						<Button
							type="button"
							variant="default"
							size="sm"
							className="flex-1"
							onClick={handleConfirmUpload}
							disabled={isUploading}
						>
							<Check className="mr-2 h-4 w-4" />
							ยืนยันการอัปโหลด
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="flex-1"
							onClick={handleClearPreview}
							disabled={isUploading}
						>
							<X className="mr-2 h-4 w-4" />
							ยกเลิก
						</Button>
					</div>
				)}

				{/* Hidden file input */}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/png"
					onChange={handleFileSelect}
					className="hidden"
					disabled={isUploading}
				/>

				{/* Loading state */}
				{isUploading && (
					<p className="text-center text-sm text-muted-foreground">
						กำลังอัปโหลด...
					</p>
				)}

				{/* Help text */}
				{!previewUrl && (
					<div className="space-y-2">
						<p className="text-sm text-muted-foreground">
							รองรับไฟล์ PNG เท่านั้น
						</p>
						<div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
							<p className="text-sm text-amber-800 dark:text-amber-200">
								<strong>หมายเหตุ:</strong> โลโก้ต้องมีขนาด 512x512 พิกเซล
								โดยตัวโลโก้ควรมีอัตราส่วนประมาณ 2:1 (รูปภาพมีโลโก้อยู่ตรงกลาง
								และพื้นที่โปร่งใสด้านบนและด้านล่าง) หากรูปโลโก้ไม่ใช่ขนาด
								512x512 ปุ่ม &quot;ติดตั้งแอป&quot; จะไม่แสดงบนเว็บไซต์ลูกค้า
							</p>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
